import { useState } from "react";
import { useTranslation } from "react-i18next";

import { mapApiError } from "../../lib/api";

import { type ReportType } from "./api";
import { buildPayload } from "./buildPayload";
import { useCreateReport } from "./hooks";
import { SuccessFeedback } from "./SuccessFeedback";
import { TurnstileChallenge } from "./TurnstileChallenge";

const DURATIONS_MIN = [15, 30, 45, 60] as const;
const CARS_WAITING = [1, 2, 3] as const;

type Props = {
  stationId: string;
};

type Status =
  | { kind: "idle" }
  | { kind: "success"; helpedCount?: number; isFirstToday?: boolean }
  | { kind: "rate-limited"; retryAfter: number }
  | { kind: "spam-detected" }
  | { kind: "validation"; fields?: unknown }
  | { kind: "unauthorized" }
  | { kind: "network" }
  | { kind: "server" }
  | { kind: "error"; message: string };

const BLOCKED_STATES: Status["kind"][] = ["rate-limited", "spam-detected"];

export function ReportForm({ stationId }: Props) {
  const { t } = useTranslation();
  const mutation = useCreateReport(stationId);
  const [type, setType] = useState<ReportType | null>(null);
  const [duration, setDuration] = useState<(typeof DURATIONS_MIN)[number]>(30);
  const [carsWaiting, setCarsWaiting] = useState<(typeof CARS_WAITING)[number]>(1);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function submit(turnstileToken?: string) {
    if (!type) return;

    const payload = buildPayload({ stationId, type, duration, carsAhead: carsWaiting });
    const body = turnstileToken
      ? { ...payload, cf_turnstile_response: turnstileToken }
      : payload;
    try {
      const out = await mutation.mutateAsync(body);
      setStatus({
        kind: "success",
        helpedCount: out?.helped_count,
        isFirstToday: out?.is_first_today,
      });
      setType(null);
    } catch (err) {
      setStatus(mapApiError(err));
    }
  }

  const buttonDisabled = !type || mutation.isPending || BLOCKED_STATES.includes(status.kind);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-4"
    >
      <fieldset className="space-y-2" aria-label={t("stations.report_heading")}>
        <div className="grid grid-cols-2 gap-2">
          <TypeButton current={type} value="available" label={t("reports.type_available")} onPick={setType} />
          <TypeButton current={type} value="charging" label={t("reports.type_charging")} onPick={setType} />
          <TypeButton current={type} value="waiting" label={t("reports.type_waiting")} onPick={setType} />
          <TypeButton current={type} value="offline" label={t("reports.type_offline")} onPick={setType} />
        </div>
      </fieldset>

      {type === "charging" && (
        <fieldset>
          <legend className="text-sm font-semibold text-fg-mute">{t("reports.duration_label")}</legend>
          <select
            aria-label={t("reports.duration_label")}
            value={duration}
            onChange={(e) => setDuration(parseDuration(e.target.value))}
            className="mt-1 w-full rounded-md border border-line px-3 py-2 text-sm"
          >
            {DURATIONS_MIN.map((m) => (
              <option key={m} value={m}>
                {t("reports.duration_minutes", { n: m })}
              </option>
            ))}
          </select>
        </fieldset>
      )}

      {type === "waiting" && (
        <fieldset>
          <legend className="text-sm font-semibold text-fg-mute">{t("reports.cars_waiting_label")}</legend>
          <select
            aria-label={t("reports.cars_waiting_label")}
            value={carsWaiting}
            onChange={(e) => setCarsWaiting(parseCarsWaiting(e.target.value))}
            className="mt-1 w-full rounded-md border border-line px-3 py-2 text-sm"
          >
            {CARS_WAITING.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </fieldset>
      )}

      <StatusBanner status={status} onTurnstileVerify={(token) => void submit(token)} />

      <button
        type="submit"
        disabled={buttonDisabled}
        className="rounded-md bg-bg-deep px-4 py-2 text-sm font-medium text-fg hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-50"
      >
        {mutation.isPending ? t("reports.submit_sending") : t("reports.submit_send")}
      </button>
    </form>
  );
}

function StatusBanner({
  status,
  onTurnstileVerify,
}: {
  status: Status;
  onTurnstileVerify: (token: string) => void;
}) {
  const { t } = useTranslation();
  if (status.kind === "success") {
    return <SuccessFeedback helpedCount={status.helpedCount} isFirstToday={status.isFirstToday} />;
  }
  if (status.kind === "rate-limited") {
    return (
      <p role="alert" className="text-sm text-ember">
        {t("errors.rate_limited", { seconds: status.retryAfter })}
      </p>
    );
  }
  if (status.kind === "spam-detected") {
    return <TurnstileChallenge onVerify={onTurnstileVerify} />;
  }
  if (status.kind === "validation") {
    return <p role="alert" className="text-sm text-ember">{t("errors.validation")}</p>;
  }
  if (status.kind === "unauthorized") {
    return <p role="alert" className="text-sm text-ember">{t("errors.unauthorized")}</p>;
  }
  if (status.kind === "network") {
    return <p role="alert" className="text-sm text-ember">{t("errors.network")}</p>;
  }
  if (status.kind === "server") {
    return <p role="alert" className="text-sm text-ember">{t("errors.server")}</p>;
  }
  if (status.kind === "error") {
    return (
      <p role="alert" className="text-sm text-ember">
        {t("reports.send_report_failed_with_reason", { reason: status.message || t("errors.unknown") })}
      </p>
    );
  }
  return null;
}

function parseDuration(value: string): (typeof DURATIONS_MIN)[number] {
  const n = Number(value);
  return DURATIONS_MIN.find((d) => d === n) ?? 30;
}

function parseCarsWaiting(value: string): (typeof CARS_WAITING)[number] {
  const n = Number(value);
  return CARS_WAITING.find((c) => c === n) ?? 1;
}

function TypeButton({
  current,
  value,
  label,
  onPick,
}: {
  current: ReportType | null;
  value: ReportType;
  label: string;
  onPick: (v: ReportType) => void;
}) {
  const selected = current === value;
  return (
    <button
      type="button"
      onClick={() => onPick(value)}
      className={`rounded-md border px-3 py-2 text-sm font-medium ${
        selected
          ? "border-volt bg-bg-deep text-fg"
          : "border-line bg-surface text-fg-mute hover:border-line-strong"
      }`}
    >
      {label}
    </button>
  );
}
